import { Tooltip } from "@etframework/ui.components";
import { useRef } from "react";

// One Tooltip instance serves every cell in the grid instead of one per cell.

const GRID_TIP_CLASS = "et-taskgrid-tip";
const TIP_TEXT_ATTRIBUTE = "data-tip";
const TIP_LIST_ATTRIBUTE = "data-tip-list";
const TIP_LIST_SEPARATOR = "\n";

const withTipClass = (className) =>
  className ? `${className} ${GRID_TIP_CLASS}` : GRID_TIP_CLASS;

/**
 * Props for a cell element that shows `text` on hover.
 * Usage: <div {...gridTip(Client, "text-truncate fs-6")}>…</div>
 */
export const gridTip = (text, className) => ({
  className: withTipClass(className),
  [TIP_TEXT_ATTRIBUTE]: text ?? ""
});

/**
 * Props for a cell element that shows `items` as a bulleted list on hover.
 * Usage: <div {...gridTipList(Tags)}>…</div>
 */
export const gridTipList = (items, className) => ({
  className: withTipClass(className),
  [TIP_LIST_ATTRIBUTE]: (items ?? []).join(TIP_LIST_SEPARATOR)
});

const tipContent = (target) => {
  const list = target.getAttribute(TIP_LIST_ATTRIBUTE);

  if (list)
    return `<ul class="ps-3">${list
      .split(TIP_LIST_SEPARATOR)
      .filter((item) => item !== "")
      .map((item) => `<li>${item}</li>`)
      .join("")}</ul>`;

  const text = target.getAttribute(TIP_TEXT_ATTRIBUTE);

  return text ? text : "";
};

/**
 * Wraps a grid so a single Tooltip serves all of its cells.
 */
export const GridTooltip = ({ children, openDelay = 500 }) => {
  const tooltipRef = useRef(null);

  const onBeforeRender = (args) => {
    const content = tipContent(args.target);

    if (!content) {
      args.cancel = true;
      return;
    }

    tooltipRef.current.content = content;
  };

  return (
    <Tooltip
      ref={tooltipRef}
      target={`.${GRID_TIP_CLASS}`}
      /* OpensOn="Hover" keeps it off the Focus
       * path, which wires listeners to a static
       * element list at mount time and would miss
       * those rows.
       * */
      opensOn="Hover"
      openDelay={openDelay}
      position="TopCenter"
      showTipPointer={false}
      beforeRender={onBeforeRender}
    >
      {children}
    </Tooltip>
  );
};
